import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FC } from 'react';
import { Box, Checkbox, TableRow } from '@mui/material';
import { StyledTableCell } from 'ui/StyledTable';
import { nanoid } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { PAGE_SIZE } from 'services/constants/env';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { COLORS } from 'services/constants/colors';
import { errorsTableStructure } from '../errors-table-structure';
import { ActionSpeedDial } from './ActionSpeedDial';
import { TTableStructure, TFilter, TSortDirection } from '../types';

export type TErrorsTableHeadProps = {
	setCheckAll: (checkedAll: boolean) => void;
	checkedAll: boolean;
	indeterminate: boolean;
};

export const ErrorsHeader: FC<TErrorsTableHeadProps> = ({
	checkedAll,
	setCheckAll,
	indeterminate,
}) => {
	const tableStructure = errorsTableStructure() as TTableStructure[];

	const navigate = useNavigate();
	const location = useLocation();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);

	const filtersArray: TFilter[] = errorsTableStructure().reduce(
		(acc, item) => {
			if (navigateParams[item.selector])
				acc.push({ name: item.selector, value: navigateParams[item.selector] as string });
			return acc;
		},
		[] as { name: string; value: string }[],
	);

	const handleSort = (index: number, direction: TSortDirection) => {
		const newFilters = [...filtersArray];

		navigate(
			`${location.pathname}?${queryString.stringify({
				...navigateParams,
				sortIndex: index,
				sortDirection: direction,
				page: 1,
			})}`,
		);

		const timestampIndex = newFilters.findIndex?.((item) => item?.name === 'timestamp');
		if (timestampIndex > -1) {
			const dates = DATES_ARRAY.find((item) => item.id === newFilters?.[timestampIndex]?.value);
			if (dates?.from) {
				newFilters.splice(timestampIndex, 1);
				newFilters.push({
					name: 'timestamp',
					value: dates.from,
					comparator: '>=',
				});
				if (dates?.to) {
					newFilters.push({
						name: 'timestamp',
						value: dates.to,
						comparator: '<=',
					});
				}
			}
		}

		const schemesIndex = newFilters.findIndex?.((item) => item?.name === 'schemes');
		if (schemesIndex > -1) {
			const schemesValue = newFilters?.[schemesIndex]?.value as string;
			newFilters.splice(schemesIndex, 1);
			newFilters.push({
				name: 'schemes',
				value: `%${schemesValue}%`,
				comparator: 'like',
			});
		}

		const idIndex = newFilters.findIndex?.((item) => item?.name === 'external_order_id');
		if (idIndex > -1) {
			const idValue = newFilters?.[idIndex]?.value as string;
			newFilters.splice(idIndex, 1);
			newFilters.push({
				name: 'external_order_id',
				value: `%${idValue}%`,
				comparator: 'like',
			});
		}

		// dispatch(
		// 	getConfirmedOrdersRequest({
		// 		filter: newFilters,
		// 		orderBy: direction
		// 			? [`${tableStructure[index].selector} ${direction as string}`]
		// 			: ['id desc'],
		// 		page: {
		// 			from: 0,
		// 			size: PAGE_SIZE,
		// 		},
		// 		tableName: 'dashboard_confirmed_orders',
		// 	}),
		// );
	};

	const sort =
		navigateParams.sortIndex && navigateParams.sortDirection
			? {
					index: Number(navigateParams.sortIndex ?? 0),
					direction: (navigateParams.sortDirection ?? 'asc') as TSortDirection,
				}
			: null;

	return (
		<TableRow sx={{ borderTop: `1px solid ${COLORS.PRIMARY}70` }}>
			<StyledTableCell
				key={nanoid()}
				style={{
					padding: 0,
					backgroundColor: COLORS.BACKGROUND_SECONDARY,
				}}
			>
				<Checkbox
					checked={checkedAll}
					indeterminate={indeterminate}
					onChange={(e) => {
						e.stopPropagation();
						setCheckAll(e.target.checked);
					}}
					onClick={(e) => {
						e.stopPropagation();
					}}
					sx={{
						color: COLORS.PRIMARY,
						'&.Mui-checked': {
							color: COLORS.PRIMARY,
						},
						'&.MuiCheckbox-indeterminate': {
							color: COLORS.PRIMARY,
						},
					}}
					// onChange={handleChange}
					// inputProps={{ 'aria-label': 'primary checkbox' }}
				/>
			</StyledTableCell>

			{tableStructure?.map((filter, index: number) => {
				if (filter.show === false) return null;
				if (filter?.noName)
					return (
						<StyledTableCell
							key={nanoid()}
							style={{ padding: 0, backgroundColor: COLORS.BACKGROUND_SECONDARY }}
						/>
					);
				return (
					<StyledTableCell
						key={nanoid()}
						align={filter?.align ?? 'center'}
						colSpan={filter.colSpan ?? 1}
						style={{
							padding: '0px 5px 0px 5px',
							verticalAlign: 'top',
							backgroundColor: COLORS.BACKGROUND_SECONDARY,
						}}
					>
						<Box
							sx={{
								display: 'inline-flex',
								alignItems: 'start',
								justifyContent: 'start',
								flexDirection: 'row',
							}}
						>
							<Box
								sx={{
									display: 'block',
									m: '4px 0 4px 0',
									lineHeight: '1.4',
									textAlign: 'left',
									fontSize: '0.75rem',
									whiteSpace: 'pre-line',
									color: COLORS.PRIMARY,
								}}
							>
								{filter.name}
							</Box>
							{filter.sortable && (
								<Box
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										justifyContent: 'center',
										flexDirection: 'column',
										ml: '2px',
									}}
								>
									<KeyboardArrowUpIcon
										sx={{
											display: 'inline',
											mb: '-5px',
											mt: '0px',
											color: COLORS.PRIMARY,
											fontSize: 18,
											opacity: sort?.index === index && sort?.direction === 'asc' ? 1 : 0.3,
										}}
										fontSize="small"
									/>
									<KeyboardArrowDownIcon
										sx={{
											display: 'inline',
											mt: '-5px',
											color: COLORS.PRIMARY,
											fontSize: 18,
											opacity: sort?.index === index && sort?.direction === 'desc' ? 1 : 0.3,
										}}
										fontSize="small"
									/>
									<ActionSpeedDial
										sortable={filter.sortable}
										onChangeSort={(direction: TSortDirection) => handleSort(index, direction)}
										sortDirection={sort?.index === index ? sort?.direction : undefined}
									/>
								</Box>
							)}
						</Box>
					</StyledTableCell>
				);
			})}
		</TableRow>
	);
};
